import {
  computeDaysFromTodayToDate,
  formatDateUsMdY,
  parseDateFromBackend,
} from "../../services";

/** Notify 45 days before insurance / registration / emissions deadlines. */
export const VEHICLE_DEADLINE_NOTICE_DAYS = 45;

const notifyDateFromDeadline = (dateValue) => {
  const d = parseDateFromBackend(dateValue);
  if (!d || Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() - VEHICLE_DEADLINE_NOTICE_DAYS);
  return formatDateUsMdY(d);
};

const COMPLIANCE_DEADLINES = [
  {
    key: "insurance",
    title: "Insurance Expiration Date",
    dateOf: (vehicle) => vehicle?.insurance_start_date || vehicle?.insurance_expire_on || "",
  },
  {
    key: "registration",
    title: "Vehicle Registration Date",
    dateOf: (vehicle) => vehicle?.vehicle_registration_date || vehicle?.title_registration_on || "",
  },
  {
    key: "emissions",
    title: "Emissions",
    dateOf: (vehicle) => vehicle?.emissions_date || vehicle?.emission_test_on || "",
  },
];

export const parseMileageNumber = (value) => {
  const digits = String(value ?? "").replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) ? n : null;
};

export const formatMileageLabel = (value) => {
  const n = parseMileageNumber(value);
  if (n == null) return "";
  return n.toLocaleString();
};

const repairSortTime = (row) => {
  const raw = row?.repair_date || row?.create_date || "";
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
};

const latestReminderByPart = (repairs) => {
  const byPart = new Map();
  (repairs || []).forEach((row) => {
    const next = parseMileageNumber(row?.next_replacement_reminder);
    if (next == null) return;
    const part = `${row?.part_name || row?.repair_description || "Repair"}`.trim() || "Repair";
    const key = part.toLowerCase();
    const prev = byPart.get(key);
    if (!prev || repairSortTime(row) >= repairSortTime(prev.row)) {
      byPart.set(key, { part, next, row });
    }
  });
  return [...byPart.values()];
};

export const buildVehicleReminders = (vehicle, repairs = []) => {
  const items = [];

  COMPLIANCE_DEADLINES.forEach((def) => {
    const dateValue = def.dateOf(vehicle);
    if (!dateValue || !String(dateValue).trim()) return;
    const remainingRaw = computeDaysFromTodayToDate(dateValue);
    if (remainingRaw === "") return;
    const remaining = Number(remainingRaw);
    const deadlinePassed = Number.isFinite(remaining) && remaining <= 0;
    const exceeded =
      Number.isFinite(remaining) && remaining <= VEHICLE_DEADLINE_NOTICE_DAYS;
    const notifyDate = notifyDateFromDeadline(dateValue);
    items.push({
      id: `deadline-${def.key}:${String(dateValue).trim()}`,
      kind: "deadline",
      title: def.title,
      dateValue: String(dateValue).trim(),
      remainingDays: remainingRaw,
      exceeded,
      statusText: deadlinePassed
        ? "This deadline has been reached or passed."
        : `You will be notified on ${notifyDate || String(dateValue).trim()}.`,
    });
  });

  const currentMileage = parseMileageNumber(vehicle?.mileage);
  latestReminderByPart(repairs).forEach((entry) => {
    const exceeded =
      currentMileage != null && entry.next != null && currentMileage >= entry.next;
    const nextLabel = formatMileageLabel(entry.next);
    items.push({
      id: `repair-${entry.row?.id || entry.row?._id || entry.part}:${entry.next}`,
      kind: "repair",
      title: entry.part,
      currentMileage,
      nextMileage: entry.next,
      exceeded,
      statusText: exceeded
        ? `Mileage has reached the ${nextLabel} mile reminder.`
        : `You will be notified when it reach ${nextLabel} miles.`,
    });
  });

  const completed = new Set(
    (Array.isArray(vehicle?.completed_reminder_keys) ? vehicle.completed_reminder_keys : [])
      .map((key) => `${key || ""}`.trim())
      .filter(Boolean)
  );
  return items.filter((item) => !completed.has(item.id));
};

export const vehicleHasReminderAlert = (vehicle, repairs = []) =>
  buildVehicleReminders(vehicle, repairs).some((item) => item.exceeded);

export const repairsByVehicleId = (repairs = []) => {
  const map = new Map();
  (repairs || []).forEach((row) => {
    const id = `${row?.vehicle || ""}`.trim();
    if (!id) return;
    if (!map.has(id)) map.set(id, []);
    map.get(id).push(row);
  });
  return map;
};
